import React from "react";
import { Flame } from "lucide-react";

const SHEET_W = 816;
const SHEET_H = 1056;

const PIZZAS = [
  {
    name: "Smokey AF BBQ Pizza",
    steps: [
      "Taco Bell chipotle sauce base",
      "Bottom layer of cheese",
      "Generous helping of pulled pork",
      "Bacon bits",
      "Another helping of cheese",
      "BBQ sauce drizzled on top",
    ],
  },
  {
    name: "Chicken Alfredo Pizza",
    steps: ["Alfredo sauce base", "Cheese", "Chicken", "Cheese again"],
  },
  {
    name: "Chicken Bacon Ranch Pizza",
    steps: ["Ranch base", "Cheese", "Chicken", "Bacon", "Another layer of cheese"],
  },
  {
    name: "Pepperoni Pizza",
    steps: ["Red sauce base", "Cheese", "Pepperoni", "Cheese"],
  },
  {
    name: "Nacho Jalapeño Pork Pizza",
    steps: [
      "Nacho cheese base",
      "Cheese",
      "Pulled pork",
      "Jalapeños",
      "Another layer of cheese",
      "BBQ sauce drizzle",
    ],
  },
];

const SANDWICHES = [
  {
    name: "Pulled Pork Sandwich Meal",
    steps: [
      "Toast the bun",
      "Pulled pork on the bottom bun",
      "BBQ sauce",
      "Coleslaw",
      "Top bun",
    ],
    note: "MEAL — ADD: Fries · Ketchup · Pickle",
    noteColor: "#1F8A44",
  },
  {
    name: "Mini Pulled Pork Sandwich",
    steps: [
      "Toast the mini bun",
      "Pulled pork on the bottom bun",
      "BBQ sauce",
      "Coleslaw",
      "Top bun",
    ],
    note: "SANDWICH ONLY — NO fries, ketchup or pickle",
    noteColor: "#FF3B30",
  },
  {
    name: "BBQ Boat",
    steps: [
      "TWO roll / sub-type buns",
      "Pulled pork in each",
      "Chipotle sauce drizzled on the pork",
      "BBQ sauce drizzled on the pork",
      "Coleslaw",
      "Top bun",
    ],
    note: "ALWAYS TWO SANDWICHES",
    noteColor: "#1F8A44",
  },
  {
    name: "Mini BBQ Boat",
    steps: [
      "ONE roll / sub-type bun",
      "Pulled pork",
      "Chipotle sauce drizzled on the pork",
      "BBQ sauce drizzled on the pork",
      "Coleslaw",
      "Top bun",
    ],
    note: "SAME BUILD — JUST ONE SANDWICH",
    noteColor: "#FF3B30",
  },
  {
    name: "Japanese Pulled Pork Sandwich",
    steps: [
      "Toast top & bottom bun",
      "Generous helping of pulled pork",
      "Generous Japanese BBQ sauce",
      "Chipotle drizzle",
      "Coleslaw",
    ],
    note: "SOLD BY ITSELF — no fries or pickle",
    noteColor: "#FF3B30",
  },
  {
    name: "Nacho Jalapeño Pork Sandwich",
    steps: [
      "Toast top & bottom bun",
      "Generous helping of pulled pork",
      "Nacho cheese",
      "Jalapeños",
      "BBQ sauce drizzle",
    ],
    note: "SOLD BY ITSELF",
    noteColor: "#FF3B30",
  },
  {
    name: "BBQ Chicken Sandwich",
    steps: [
      "Toast top & bottom bun",
      "Cook chicken to 170°F internal temp",
      "Chicken on the bottom bun",
      "Top bun: Mayo",
      "BBQ sauce",
      "Sliced tomato",
      "Lettuce",
    ],
    note: "SOLD BY ITSELF",
    noteColor: "#FF3B30",
  },
];

const BOWLS = [
  {
    name: "Betty Cracker Chicken Mashed Potato Bowl",
    steps: [
      "Mashed potatoes",
      "Chicken",
      "Nacho cheese sauce",
      "BBQ sauce",
      "Bacon bits",
      "Green onions",
    ],
  },
  {
    name: "Pork Crack Mash Bowl",
    steps: [
      "Mashed potatoes",
      "Pulled pork",
      "Nacho cheese sauce",
      "BBQ sauce",
      "Bacon bits",
      "Green onions",
    ],
    note: "SAME AS BETTY — PULLED PORK, NO CHICKEN",
    noteColor: "#1F8A44",
  },
  {
    name: "Loaded Pork Fries",
    steps: [
      "Fries",
      "Pulled pork",
      "Nacho cheese",
      "BBQ sauce",
      "Bacon bits",
      "Green onions",
    ],
  },
];

const WINGS = [
  { name: "BBQ Wings", steps: ["Cook wings to 175°F or above", "Toss in BBQ sauce"] },
  { name: "Buffalo Wings", steps: ["Cook wings to 175°F or above", "Toss in buffalo sauce"] },
  {
    name: "Garlic Parmesan Wings",
    steps: ["Cook wings to 175°F or above", "Toss in garlic Parmesan sauce"],
  },
  {
    name: "Chipotle Firecracker Wings",
    steps: [
      "Cook wings to 175°F or above",
      "Mix garlic Parmesan sauce with Taco Bell chipotle sauce",
      "Toss the wings in the mix",
    ],
  },
];

const Card = ({ b }) => (
  <div className="border-2 border-black flex flex-col overflow-hidden">
    <div className="bg-black px-3.5 py-1.5">
      <h2 className="font-['Anton'] uppercase tracking-tight text-white leading-tight text-[19px]">
        {b.name}
      </h2>
    </div>
    <ol className="px-3.5 py-2 flex-1 space-y-1">
      {b.steps.map((s, i) => (
        <li key={s} className="flex items-center gap-2">
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#FF3B30] text-white font-['Anton'] text-[12px] flex items-center justify-center">
            {i + 1}
          </span>
          <span className="font-['Outfit'] font-semibold text-black text-[13.5px] leading-tight">
            {s}
          </span>
        </li>
      ))}
    </ol>
    {b.note && (
      <div
        className="px-3.5 py-1.5 font-['Anton'] uppercase tracking-wide text-white text-[12.5px]"
        style={{ background: b.noteColor }}
      >
        {b.note}
      </div>
    )}
  </div>
);

const SectionBand = ({ title, note }) => (
  <div className="col-span-2 bg-[#FF8C00] px-4 py-2 flex items-center justify-between">
    <span className="font-['Anton'] uppercase tracking-widest text-black text-[20px]">
      {title}
    </span>
    {note && (
      <span className="font-['Outfit'] font-bold uppercase tracking-wide text-black text-[12px] text-right max-w-[440px]">
        {note}
      </span>
    )}
  </div>
);

const Sheet = ({ testid, pageLabel, children }) => (
  <div
    data-testid={testid}
    className="bg-white relative flex flex-col"
    style={{ width: SHEET_W, height: SHEET_H }}
  >
    <div className="bg-black px-8 py-3.5 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Flame className="w-6 h-6 text-[#FF3B30]" />
        <span className="font-['Anton'] text-xl tracking-widest uppercase text-white">
          Smokey <span className="text-[#FF3B30]">AF</span> Barbecue
        </span>
      </div>
      <span className="font-['Anton'] text-xl tracking-widest uppercase text-[#FF8C00]">
        Build Charts · {pageLabel}
      </span>
    </div>
    <div className="h-1.5 bg-[#FF3B30]" />
    <div className="flex-1 grid grid-cols-2 gap-3 p-4 content-start">
      {children}
    </div>
    <div className="bg-black px-8 py-2 flex items-center justify-between">
      <span className="font-['Outfit'] uppercase tracking-[0.3em] text-zinc-400 text-[10px]">
        Build in number order — bottom to top
      </span>
      <span className="font-['Anton'] uppercase tracking-widest text-[#FF8C00] text-[12px]">
        Cooked Low · Served Loud
      </span>
    </div>
  </div>
);

export default function KitchenChart() {
  return (
    <div className="bg-zinc-900 flex flex-col items-center">
      <Sheet testid="kitchen-chart-sheet-1" pageLabel="Pizzas">
        <SectionBand
          title="Pizzas"
          note="Every pizza: spread the base sauce to ¼ inch from the edge · Bake 5 minutes in the oven"
        />
        {PIZZAS.map((b) => (
          <Card key={b.name} b={b} />
        ))}
      </Sheet>

      <Sheet testid="kitchen-chart-sheet-2" pageLabel="Sandwiches">
        <SectionBand title="Sandwiches & Boats" />
        {SANDWICHES.map((b) => (
          <Card key={b.name} b={b} />
        ))}
      </Sheet>

      <Sheet testid="kitchen-chart-sheet-3" pageLabel="Bowls & Wings">
        <SectionBand title="Bowls & Fries" />
        {BOWLS.map((b) => (
          <Card key={b.name} b={b} />
        ))}
        <SectionBand
          title="Wings"
          note="All wings get cooked to 175°F or above before tossing"
        />
        {WINGS.map((b) => (
          <Card key={b.name} b={b} />
        ))}
      </Sheet>
    </div>
  );
}
